import { NoopScrollStrategy } from '@angular/cdk/overlay';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { of, Subject } from 'rxjs';
import { catchError, concatMap, filter, map, take, takeUntil } from 'rxjs/operators';
import { LoadingOverlayComponent } from '../_shared/components/loading-overlay/loading-overlay.component';
import { PriceSummaryDataService } from '../_shared/components/price-summary/price-summary.data.service';
import { ProductListService } from '../_shared/components/product-list/product-list.service';
import { AppService } from '../_shared/services/app.service';
import { PermissionService } from '../_shared/services/permission.service';
import { Product, ProductGroup, ProductsService } from '../_shared/services/products.service';
import { QuoteService } from '../_shared/services/quote.service';
import { SelectedCustomerService } from '../_shared/services/selected-customer.service';
import { SnackBarService } from '../_shared/services/snack-bar.service';
import { SummaryDataService } from './summary.data.service';
import { ConfigitAssignment } from '../_shared/components/questionnaire/configit-quest-adapter/configit-types.interface';
import { PrintTypeLong } from '../_shared/models/print-type';
import { ProductFormControlType } from '../_shared/components/product-table/product-table.component';

@Component({
    selector: 'app-summary',
    templateUrl: './summary.component.html',
    styleUrls: ['./summary.component.scss'],
    standalone: false
})
export class SummaryComponent implements OnInit, OnDestroy {
    private ngUnsubscribe: Subject<void> = new Subject<void>();
    private dialogRef: MatDialogRef<LoadingOverlayComponent>;

    private quoteId: string;
    private productList: Product[];
    public selectedCustomerId: string;
    public navigationParams: any;
    public dataSource: any;
    public isLoading = true;
    public voucher: string = null;
    public callbackUrl: string;
    public secondaryCallbackUrl: string;
    public externalApp: boolean;
    public availablePrintTypes: string[];
    public printType: PrintTypeLong;
    public quotationRequestId: string;
    public primaryScheme: string;

    constructor(
        private appService: AppService,
        private titleService: Title,
        private productsService: ProductsService,
        private router: Router,
        private priceSummaryDataService: PriceSummaryDataService,
        private snackBarService: SnackBarService,
        private translateService: TranslateService,
        private activatedRoute: ActivatedRoute,
        private selectedCustomerService: SelectedCustomerService,
        private quoteService: QuoteService,
        private dataService: SummaryDataService,
        private productListService: ProductListService,
        public permissionService: PermissionService,
        private dialog: MatDialog
    ) {}

    public ngOnInit() {
        this.scrollToTop();
        const { id, callbackUrl, secondaryCallbackUrl, salesforceId, quotationRequestId } =
            this.activatedRoute.snapshot.queryParams;

        this.appService.salesOrg$
            .pipe(
                takeUntil(this.ngUnsubscribe),
                concatMap((salesOrg) => this.dataService.getAvailablePrintTypes(salesOrg))
            )
            .subscribe((printTypes) => {
                this.availablePrintTypes = printTypes;
                // default should be 'L' if available, otherwise use 'M' or 'S'
                this.printType = <PrintTypeLong>['LONG', 'MIDDLE', 'SHORT'].find((t) => printTypes.includes(t));
            });
        this.quoteId = id;
        this.callbackUrl = callbackUrl;
        this.secondaryCallbackUrl = secondaryCallbackUrl;
        if (salesforceId) {
            this.appService.salesforceId$.next(salesforceId);
        }
        if (quotationRequestId) {
            this.quotationRequestId = quotationRequestId;
        }

        this.navigationParams = {
            queryParams: {
                id: this.quoteId,
                ...(quotationRequestId && { quotationRequestId }),
            },
        };

        this.quoteService
            .fetchQuote(id)
            .pipe(take(1), takeUntil(this.ngUnsubscribe))
            .subscribe((quote) => {
                this.externalApp = !!quote.externalApp;
                this.primaryScheme = quote.schemePdfs?.primary?.split(';')[0];
                this.isLoading = false;
                if (this.externalApp) {
                    this.appService.appTitle$.next(quote.externalApp);
                    this.titleService.setTitle(quote.externalApp);
                }
            });
        this.subscribeToProductList$();
        this.subscribeToSelectedCustomer$();
    }

    public priceSummaryIsLoading() {
        return this.priceSummaryDataService.isPriceSummaryLoading$;
    }

    public priceSummaryHasErrors() {
        return this.priceSummaryDataService.hasErrors$;
    }

    public updateVoucherCode(event: string) {
        this.voucher = event;
    }

    public closeOffer() {
        // if currently logged in user is a customer, just use the viCompanyId of the currently logged in user
        const isCustomerLoggedIn =
            this.permissionService.userInfo$.value && this.permissionService.userInfo$.value.role === 'customer';
        let customerIdForClose = 'no-customer-selected';
        if (this.selectedCustomerId) {
            customerIdForClose = this.selectedCustomerId;
        } else if (isCustomerLoggedIn) {
            // use the viCompanyId
            customerIdForClose = this.permissionService.userInfo$.value.viCompanyId;
        }

        this.isLoading = true;

        this.quoteService
            .updatePrintType(this.printType)
            .pipe(
                concatMap(() => this.closeQuote(customerIdForClose)),
                map((res: { salesDocumentNumber: string }) => res.salesDocumentNumber.toString()),
                concatMap((offerNumber) => {
                    const filterSapAssignments = (assignments: ConfigitAssignment[]) =>
                        assignments.filter((a) => !a.variableName.startsWith('SapData'));
                    const quote = this.quoteService.getCurrentQuote();
                    const lines = quote?.isLegacy
                        ? (quote?.configitQuote?.lines || [])
                              .filter((l) => !l.isSubBomItem)
                              .map((line) => ({
                                  variant: line.variantCode,
                                  assignments: filterSapAssignments(line.assignments),
                              }))
                        : quote?.quotationQuote.offer.extraData.lines.map((line) => ({
                              variant: line.material,
                              assignments: filterSapAssignments(line.assignments),
                          }));
                    const reportingPaylad = {
                        projectId: quote?.projectId,
                        title: quote?.customerPurchaseNumber,
                        offerNumber,
                        data: { lines },
                    };
                    return this.dataService.addReportingEntry(reportingPaylad).pipe(
                        map(() => offerNumber),
                        catchError(() => of(offerNumber))
                    );
                })
            )
            .subscribe(
                (sapId) => {
                    this.onSuccess('SUMMARY.SNACK_BAR.CLOSE_OFFER.SUCCESS');
                    this.router.navigate(['next-steps'], {
                        queryParams: {
                            id: this.quoteId,
                            sapDocumentId: sapId,
                            callbackUrl: this.secondaryCallbackUrl,
                            ...(this.externalApp && { externalApp: this.externalApp }),
                        },
                    });
                },
                () => this.onError('SUMMARY.SNACK_BAR.CLOSE_OFFER.ERROR')
            );
    }

    public navigateBackToConfiguration() {
        this.router.navigate(['configuration'], this.navigationParams);
    }

    private closeQuote(customerNumber: string) {
        const groupedProducts: ProductGroup[] = this.productsService.getProductListViewModel(this.productList, false);
        return this.dataService.closeQuote(
            customerNumber,
            this.voucher,
            this.quotationRequestId,
            groupedProducts,
            this.priceSummaryDataService.getLastPriceSummary()
        );
    }

    private subscribeToSelectedCustomer$() {
        this.selectedCustomerService.selectedCustomer$
            .pipe(takeUntil(this.ngUnsubscribe), filter(Boolean))
            .subscribe((customer: { number: string }) => {
                this.selectedCustomerId = customer.number;
            });
    }

    private subscribeToProductList$() {
        this.openDialog();
        this.productListService.productList$.pipe(takeUntil(this.ngUnsubscribe), filter(Boolean)).subscribe({
            next: (products: Product[]) => {
                this.productList = products;
                const productList = this.productsService
                    .getProductListViewModel(products, true)
                    .map((product: Product) => {
                        const formGroup = product.material
                            ? new FormGroup<ProductFormControlType>({
                                  optional: new FormControl<boolean>({
                                      value: product.optional,
                                      disabled: !product.canBeOptional,
                                  }),
                              })
                            : undefined;
                        return {
                            ...product,
                            formGroup,
                        };
                    });

                this.dataSource = new MatTableDataSource(productList);
                this.closeDialog();
            },
            error: () => {
                this.onError('CONFIGURATION.SNACK_BAR.FETCH_CONFIGURATION.ERROR');
                this.closeDialog();
            },
        });
    }

    private onError(translationKey: string) {
        this.isLoading = false;
        this.snackBarService.openSnackBar({
            message: this.translateService.instant(translationKey),
            isFailure: true,
        });
    }

    private onSuccess(translationKey: string) {
        this.isLoading = false;
        this.snackBarService.openSnackBar({
            message: this.translateService.instant(translationKey),
        });
    }

    public ngOnDestroy(): void {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }

    private openDialog(): void {
        if (!this.dialogRef) {
            const dialogConfig = new MatDialogConfig();
            dialogConfig.disableClose = true;
            dialogConfig.width = '250px';
            dialogConfig.scrollStrategy = new NoopScrollStrategy();
            this.dialogRef = this.dialog.open(LoadingOverlayComponent, dialogConfig);
        }
    }

    private closeDialog(): void {
        if (this.dialogRef) {
            this.dialogRef.close();
            this.dialogRef = null;
        }
    }

    private scrollToTop() {
        window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    }
}
